import type {
  FirstPayerRow,
  FirstPaymentCohortRow,
  FirstPaymentCohortsResponse,
} from "@voice2mail/shared/v2m_api";

import { useSortableRows } from "../hooks/useSortableRows";
import { MetricCard } from "./MetricCard";
import { SortableTh } from "./SortableTh";

interface FirstPaymentCohortsProps {
  data: FirstPaymentCohortsResponse;
}

type CohortSortKey = "cohortMonth" | "newPayers";
const COHORT_ACCESSORS: Record<CohortSortKey, (r: FirstPaymentCohortRow) => string | number> = {
  cohortMonth: (r) => r.cohortMonth,
  newPayers: (r) => r.newPayers,
};

const TD = "px-4 py-3 text-sm text-slate-900";

function formatTierBreakdown(byTier: Record<string, number>): string {
  const entries = Object.entries(byTier).sort(([a], [b]) => a.localeCompare(b));
  if (entries.length === 0) return "—";
  return entries.map(([tier, count]) => `${tier}: ${count}`).join(", ");
}

function CohortTable({ rows }: { rows: FirstPaymentCohortRow[] }) {
  // Newest cohort first — same "biggest/newest first is the useful first
  // click" convention as UnitEconomics' cost-descending default.
  const { sorted, sort, toggleSort } = useSortableRows(rows, COHORT_ACCESSORS, {
    key: "cohortMonth",
    direction: "desc",
  });

  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <SortableTh
                label="Miesiąc kohorty"
                sortKey="cohortMonth"
                sort={sort}
                onSort={toggleSort}
                sticky
              />
              <SortableTh
                label="Nowi płacący"
                sortKey="newPayers"
                sort={sort}
                onSort={toggleSort}
              />
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Rozbicie wg poziomu
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {sorted.map((row) => (
              <tr key={row.cohortMonth} className="group hover:bg-slate-50 transition-colors">
                <td
                  className={`${TD} sticky left-0 z-10 bg-white group-hover:bg-slate-50 font-mono`}
                >
                  {row.cohortMonth}
                </td>
                <td className={TD}>{row.newPayers}</td>
                <td className="px-4 py-3 text-sm text-slate-600">
                  {formatTierBreakdown(row.byTier)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RecentFirstPayersList({ rows }: { rows: FirstPayerRow[] }) {
  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="sticky left-0 z-10 bg-slate-50 px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Użytkownik
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Pierwsza płatność
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Poziom
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {rows.map((row) => (
              <tr key={row.userId} className="group hover:bg-slate-50 transition-colors">
                <td
                  className={`${TD} sticky left-0 z-10 bg-white group-hover:bg-slate-50 font-mono text-xs`}
                >
                  {row.userId}
                </td>
                <td className={TD}>{row.firstPaidAt}</td>
                <td className="px-4 py-3 text-sm text-slate-600">{row.firstPaidTier}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * V2M-DEV-012, signal #5 ("firstPaidAt / firstPaidTier") — the one signal in
 * the 12-signal metrics project with zero code before `GET
 * /admin/metrics/first-payment-cohorts` landed. This component is that
 * endpoint's first (and only) panel consumer.
 *
 * At today's near-zero paying-user base an empty `cohorts` array is the
 * EXPECTED, correct result — PK's own instruction: the panel must show
 * "zmierzone zero" (measured zero), never a blank table indistinguishable
 * from a broken/unmeasured pipeline. Unlike `UnitEconomics` (which has NO
 * backend signal to make that distinction and must render an honest
 * disclosure of the ambiguity), this endpoint DOES carry a real signal —
 * `writeFailuresTotal`, sourced from `revenue_ledger_write_failure_stats` —
 * so this component renders a genuine, resolved answer instead of punting:
 * `writeFailuresTotal === 0` means the zero is trustworthy; a non-zero value
 * means `revenue_ledger` writes have been silently failing and the cohort
 * count (whatever it is) may be an undercount, which is a materially
 * different, louder warning than "no customers yet".
 */
export function FirstPaymentCohorts({ data }: FirstPaymentCohortsProps) {
  const hasWriteFailures = data.writeFailuresTotal > 0;
  const hasCohorts = data.cohorts.length > 0;

  return (
    <div className="space-y-6">
      {hasWriteFailures && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4" role="alert">
          <div className="text-red-800 text-sm font-medium">
            {data.writeFailuresTotal} nieudanych zapisów do <code>revenue_ledger</code> — liczba
            płacących poniżej może być zaniżona (zawodny pomiar, nie zmierzone zero).
          </div>
        </div>
      )}

      {!hasCohorts && !hasWriteFailures && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4" role="status">
          <div className="text-slate-700 text-sm font-medium">
            Zmierzone zero — nikt jeszcze nie dokonał pierwszej płatności. Zero zapisów w{" "}
            <code>revenue_ledger</code> i zero nieudanych zapisów — to jest realny wynik pomiaru,
            nie brak pomiaru.
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:max-w-2xl">
        <MetricCard label="Płacący ogółem (all-time)" value={data.totalPaidUsers} />
      </div>

      {hasCohorts && <CohortTable rows={data.cohorts} />}

      {data.recentFirstPayments.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-semibold text-slate-700">Ostatnie pierwsze płatności</h4>
          <RecentFirstPayersList rows={data.recentFirstPayments} />
        </div>
      )}
    </div>
  );
}
